/**
 * **A11 · The role-holder badge** (Stream A, Paper.dc.html), laid out by the `role_badges` engine:
 * one 3×4 badge a person holding a role this year, from Festival Admin → Roles (named people only:
 * an address is not a name), with the radio plan's channels. The volunteer's badge is the shift
 * tag the booth prints. Scored against the drawn chair and director badges by walk 99g.
 */
import type { ReactNode } from 'react';
import { roleBadges, type RadioPlan } from '@cider/domain/engines';
import { useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';

interface RoleRow {
  personId: string;
  name: string;
  role: string;
  roleName: string;
  areaName?: string | null;
  named?: boolean;
  state: string;
}

export function RoleBadges({ year }: { year: string }): ReactNode {
  const d = useLoad<RoleRow[]>(`/admin/roles/year?year=${year}`);
  const plan = useLoad<RadioPlan>(`/ops/radio-plan?year=${year}`);
  const ready = !!d.data && (plan.data !== null || plan.error !== null);
  usePrinted('role-badges', ready, { year: Number(year) });
  if (d.error) return <p role="alert">{d.error}</p>;
  if (!ready || !d.data) return <p>Reading the roles…</p>;
  const live = d.data.filter(
    (r) => (r.state === 'granted' || r.state === 'active') && r.named !== false,
  );
  const people = [...new Set(live.map((r) => r.personId))];
  if (people.length === 0) return <p>No one holds a role for {year} yet.</p>;
  const p = plan.data;
  return (
    <DrawnPages
      pages={roleBadges(
        Number(year),
        people.map((pid) => {
          const mine = live.filter((r) => r.personId === pid);
          return {
            name: mine[0]?.name ?? '',
            roles: mine.map((r) => ({
              role: r.role,
              roleName: r.roleName,
              area: r.areaName ?? null,
            })),
          };
        }),
        p
          ? {
              chairs: p.channels.find((c) => /chairs/i.test(c.use))?.channel ?? null,
              security: p.securityChannel,
            }
          : null,
      )}
      size="badge"
      stamp="time"
    />
  );
}
